import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { InfoIcon, TrendingUp, TrendingDown } from "lucide-react";
import { ExchangeRateDto } from "@/types/exchangeRate";
import { useUpdateExchangeRate } from "@/hooks/useExchangeRateQueries";
import {
  formatExchangeRate,
  formatConversionExample,
  isValidExchangeRate,
  calculateRateChange,
  formatPercentageChange,
} from "@/lib/currencyUtils";

interface UpdateExchangeRateDialogProps {
  rate: ExchangeRateDto;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Update Exchange Rate Dialog
 *
 * Allows admins to update an exchange rate with:
 * - Real-time preview of new rate
 * - Example conversion (₫1,000,000 = X USD)
 * - Percentage change indicator
 * - Optional notes for audit trail
 * - Validation for rate > 0
 */
export function UpdateExchangeRateDialog({
  rate,
  open,
  onClose,
  onSuccess,
}: UpdateExchangeRateDialogProps) {
  const [newRate, setNewRate] = useState("");
  const [notes, setNotes] = useState("");
  const [validationError, setValidationError] = useState("");

  const { mutate: updateRate, isPending } = useUpdateExchangeRate();

  // Reset form when dialog opens with new rate
  useEffect(() => {
    if (open) {
      setNewRate(rate.rateToVND.toString());
      setNotes("");
      setValidationError("");
    }
  }, [open, rate]);

  const handleRateChange = (value: string) => {
    setNewRate(value);
    setValidationError("");

    // Validate rate
    const numericRate = parseFloat(value);
    if (value && (!isValidExchangeRate(numericRate) || numericRate <= 0)) {
      setValidationError("Exchange rate must be a positive number");
    }
  };

  const handleSubmit = async () => {
    const numericRate = parseFloat(newRate);

    // Validate
    if (!newRate || !isValidExchangeRate(numericRate) || numericRate <= 0) {
      setValidationError("Please enter a valid exchange rate");
      return;
    }

    try {
      await updateRate.mutateAsync({
        currency: rate.currency,
        request: {
          rate: numericRate,
          source: "Manual",
          notes: notes.trim() || undefined,
          version: rate.version,
        },
      });
      onSuccess();
      onClose();
    } catch {
      // Error handling is done in the mutation via toast
    }
  };

  const numericNewRate = parseFloat(newRate);
  const isRateValid =
    newRate && isValidExchangeRate(numericNewRate) && numericNewRate > 0;
  const rateChange = isRateValid
    ? calculateRateChange(rate.rateToVND, numericNewRate)
    : 0;
  const hasChanged = isRateValid && numericNewRate !== rate.rateToVND;
  const isDirty = hasChanged || notes.trim().length > 0;

  const guard = useCloseGuard({
    open,
    onOpenChange: onClose,
    isDirty,
    isSubmitting: isPending,
    onDiscard: () => {
      setNewRate(rate.rateToVND.toString());
      setNotes("");
    },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Update {rate.currency} Exchange Rate</DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <DialogDescription>
              Update the exchange rate for {rate.currency} to VND. Changes will
              be applied immediately system-wide.
            </DialogDescription>
            {/* Current Rate Info */}
            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertTitle>Current Rate</AlertTitle>
              <AlertDescription>
                <div className="mt-2 space-y-1">
                  <div className="font-mono text-sm font-medium">
                    {rate.displayRate}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Version: v{rate.version}
                  </div>
                </div>
              </AlertDescription>
            </Alert>

            {/* New Rate Input */}
            <div className="space-y-2">
              <Label htmlFor="newRate">
                New Exchange Rate{" "}
                <span className="text-destructive-ink">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  1 {rate.currency} =
                </span>
                <Input
                  id="newRate"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="23250.00"
                  value={newRate}
                  onChange={(e) => handleRateChange(e.target.value)}
                  className={validationError ? "border-destructive" : ""}
                />
                <span className="text-sm text-muted-foreground">VND</span>
              </div>
              {validationError && (
                <p className="text-sm text-destructive-ink">
                  {validationError}
                </p>
              )}
            </div>

            {/* Real-time Preview */}
            {isRateValid && (
              <Card className="p-4 space-y-3 bg-muted/50">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    New Rate Preview
                  </p>
                  <p className="font-mono text-sm font-medium">
                    {formatExchangeRate(rate.currency, numericNewRate)}
                  </p>
                </div>

                {hasChanged && (
                  <div className="flex items-center gap-2">
                    {rateChange > 0 ? (
                      <TrendingUp className="h-4 w-4 text-ok-ink" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-destructive-ink" />
                    )}
                    <span
                      className={`text-sm font-medium ${
                        rateChange > 0 ? "text-ok-ink" : "text-destructive-ink"
                      }`}
                    >
                      {formatPercentageChange(rateChange)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {rateChange > 0 ? "increase" : "decrease"}
                    </span>
                  </div>
                )}

                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Example Conversion
                  </p>
                  <p className="text-sm">
                    {formatConversionExample(
                      1000000,
                      rate.currency,
                      numericNewRate,
                    )}
                  </p>
                </div>
              </Card>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add a note explaining this rate change..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">
                {notes.length}/500 characters
              </p>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isRateValid || !hasChanged || isPending}
            >
              {isPending ? "Updating..." : "Update Rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
